// Single source of truth for the /overview Transformation OS page.
// All module, agent, foundation, and outcome content lives here - the page renders from this object.

export const WORKING_ROOM = {
  kicker: "Operating environment",
  title: "The SIX Transformation OS",
  subtitle:
    "One system runs the entire Brightspeed engagement: evidence in, senior judgment applied, actions out, outcomes measured. Every workspace in the left panel is a live surface of this architecture - nothing here is a slide.",
  mission: {
    label: "Engagement mission",
    value: "Order Cancellation Rate 18% → 15%",
    note: "Q3 checkpoint 15.5% · ~$13M EBITDA per point (finance validation pending)",
  },
  stats: [
    { label: "Operating stages", value: "5", tone: "crimson" },
    { label: "Analysis agents", value: "12", tone: "ai" },
    { label: "Senior lenses", value: "3", tone: "neutral" },
    { label: "Live workspaces", value: "16", tone: "validated" },
  ],
};

export const SIX_FIT_ZONES = [
  {
    id: "zone-diagnose",
    tone: "ai",
    title: "Between your data and your findings",
    role: "Diagnostic zone",
    body: "SIX turns raw operational evidence - order extracts, save-team artifacts, vendor scorecards, process documents - into confidence-labeled findings. Twelve agents do the heavy processing; every output carries its evidence and its confidence state.",
    links: [
      { to: "/evidence", label: "Evidence Room" },
      { to: "/agents", label: "Analysis Studio" },
      { to: "/analytics", label: "Analytics Workspace" },
    ],
  },
  {
    id: "zone-judge",
    tone: "crimson",
    title: "Between analysis and commitment",
    role: "Judgment zone",
    body: "Three senior lenses - Transformation Partner, AI/Data Strategist, The Operator - interrogate every priority finding before it earns a recommendation. Challenge mode asks what would disprove it. Nothing reaches a register on machine output alone.",
    links: [
      { to: "/triad", label: "Triad Review Room" },
      { to: "/matrix", label: "Assessment Matrix" },
      { to: "/control-tower", label: "Control Tower" },
    ],
  },
  {
    id: "zone-deliver",
    tone: "validated",
    title: "Between commitment and measured outcome",
    role: "Delivery zone",
    body: "Confirmed gaps convert to owned actions. Actions become gated pilots with holdout measurement. Adoption is engineered, not hoped for. A 30-day rhythm carries everything to the Day 15 pulse and the Day 30 proof report.",
    links: [
      { to: "/pilots", label: "Pilot Board" },
      { to: "/brightspeed", label: "Change Management" },
      { to: "/impact-engine", label: "30-Day Engine" },
      { to: "/command-center", label: "Command Center" },
    ],
  },
];

export const OPERATING_STAGES = [
  {
    id: "stage-orient",
    num: "01",
    name: "Orient",
    purpose: "Establish the operating thesis, the three senior lenses, and the model that turns judgment into execution.",
    input: "Problem statement, context conversations",
    output: "Shared operating thesis",
    modules: [
      { to: "/briefing", label: "Consortium Briefing" },
      { to: "/flow", label: "Judgment to Execution" },
    ],
  },
  {
    id: "stage-analyze",
    num: "02",
    name: "Ingest & Analyze",
    purpose: "Request and receive evidence, run twelve agent lanes over it, and put every priority finding through triad review.",
    input: "22-category evidence register",
    output: "Validated findings with confidence",
    modules: [
      { to: "/evidence", label: "Evidence Room" },
      { to: "/launch", label: "Launch Console" },
      { to: "/agents", label: "Agentic Analysis Studio" },
      { to: "/triad", label: "Triad Review Room" },
    ],
  },
  {
    id: "stage-assess",
    num: "03",
    name: "Assess",
    purpose: "Rate 23 operating dimensions, expose the cancellation drivers, and separate gaps from risks from issues.",
    input: "Validated findings",
    output: "Rated matrix + separated registers",
    modules: [
      { to: "/matrix", label: "Assessment Matrix" },
      { to: "/analytics", label: "Analytics Workspace" },
      { to: "/control-tower", label: "Gap / Risk Control Tower" },
    ],
  },
  {
    id: "stage-execute",
    num: "04",
    name: "Execute",
    purpose: "Convert gaps into owned actions, launch gated pilots with measurement locked, and engineer adoption by plan.",
    input: "Registers + owner commitments",
    output: "Live pilots + 30-day rhythm",
    modules: [
      { to: "/questions", label: "Question Builder" },
      { to: "/pilots", label: "Pilot Board" },
      { to: "/brightspeed", label: "Change Management" },
      { to: "/impact-engine", label: "30-Day Impact Engine" },
    ],
  },
  {
    id: "stage-govern",
    num: "05",
    name: "Govern",
    purpose: "Run the executive cadence: outcomes, open decisions, acid-tested communication, and proof-standard reporting.",
    input: "Pilot telemetry + decision queue",
    output: "Day 15 pulse · Day 30 proof",
    modules: [
      { to: "/command-center", label: "Command Center" },
      { to: "/comms", label: "Communications Studio" },
      { to: "/reports", label: "Report Builder" },
    ],
  },
];

export const AGENT_LAYER = {
  intro:
    "Twelve specialized agents process the evidence in parallel lanes. Each reports upward into the three senior personas - AI accelerates the analysis, it never owns the conclusion. Every agent output carries the evidence it used, the confidence it earned, and the follow-ups it triggered.",
  groups: [
    {
      id: "grp-integrity",
      name: "Evidence & Integrity",
      tone: "ai",
      agents: [
        { id: "AGT-01", name: "Metric Integrity Agent", covers: "Definition variance, baseline reconciliation" },
        { id: "AGT-02", name: "Prior Work Intake Agent", covers: "Initiative inventory, duplicate detection" },
      ],
    },
    {
      id: "grp-diagnosis",
      name: "Diagnosis",
      tone: "ai",
      agents: [
        { id: "AGT-03", name: "Root Cause Agent", covers: "Driver trees, fallout pathways" },
        { id: "AGT-04", name: "Customer Journey Agent", covers: "Signature-to-install experience, silence windows" },
        { id: "AGT-05", name: "Process & Ownership Agent", covers: "Handoffs, escalation paths, ownership gaps" },
      ],
    },
    {
      id: "grp-opsystem",
      name: "Operating System",
      tone: "risk",
      agents: [
        { id: "AGT-06", name: "People & Organization Agent", covers: "Capacity, skills, incentives" },
        { id: "AGT-07", name: "Tools & Technology Agent", covers: "Order stack, fallout queues, integrations" },
        { id: "AGT-08", name: "Risk & Issue Agent", covers: "Exposure register, live issue triage" },
      ],
    },
    {
      id: "grp-delivery",
      name: "Delivery",
      tone: "validated",
      agents: [
        { id: "AGT-09", name: "Communications & Change Agent", covers: "Channel drafts, adoption signals" },
        { id: "AGT-10", name: "Pilot Design Agent", covers: "Nine-dimension scoring, cohort selection" },
        { id: "AGT-11", name: "Measurement Agent", covers: "Holdout design, leading indicators" },
      ],
    },
    {
      id: "grp-synthesis",
      name: "Synthesis",
      tone: "crimson",
      agents: [
        { id: "AGT-12", name: "Executive Synthesis Agent", covers: "Cross-lane convergence, command center feed" },
      ],
    },
  ],
  routing: [
    { step: "Evidence enters", detail: "22 request categories, sensitivity-flagged, owner-routed", tone: "neutral" },
    { step: "Agents process", detail: "12 lanes run in parallel - each output evidence-linked and confidence-labeled", tone: "ai" },
    { step: "Personas interrogate", detail: "TP / DS / TO each read the same finding through a different lens", tone: "crimson" },
    { step: "Triad converges", detail: "Consensus block: what we believe, what we know, what we don't, what to do", tone: "risk" },
    { step: "Registers receive", detail: "Gaps, risks, issues, actions - separated, owned, and measured", tone: "validated" },
  ],
  personas: [
    { code: "TP", name: "Transformation Partner", lens: "Business value, governance, benefit realization" },
    { code: "DS", name: "AI/Data Strategist", lens: "Evidence, confidence, measurement design" },
    { code: "TO", name: "The Operator", lens: "Operating reality, frontline, customer" },
  ],
};

export const FOUNDATIONS = [
  {
    id: "fnd-confidence",
    tone: "ai",
    name: "Confidence labeling",
    rule: "Every finding, chart, and recommendation carries its evidence state: evidence-backed, hypothesis, or data missing. No claim travels without its label.",
  },
  {
    id: "fnd-separation",
    tone: "risk",
    name: "Gap ≠ Risk ≠ Issue",
    rule: "Gaps, risks, and issues live in separated registers because they demand different treatment - conversion, mitigation, and triage respectively.",
  },
  {
    id: "fnd-holdout",
    tone: "validated",
    name: "Holdout measurement",
    rule: "No rate movement is claimed without control cohorts. The first twenty days are managed on leading indicators; Day 30 is a proof standard, not a promise.",
  },
  {
    id: "fnd-decisions",
    tone: "risk",
    name: "Decision SLA",
    rule: "Leadership decisions are queued with owners and dates. Anything open past its gate escalates - decision velocity is managed like a delivery metric.",
  },
  {
    id: "fnd-acid",
    tone: "crimson",
    name: "Acid-tested communication",
    rule: "Nothing leaves the building without the acid test: is it true, is it labeled, does it separate fact from hypothesis, does it carry a next step.",
  },
  {
    id: "fnd-adoption",
    tone: "validated",
    name: "Adoption by design",
    rule: "Every pilot ships with adoption metrics and pause-and-fix thresholds. If script adherence drops below seventy percent, the pilot pauses. No silent failure.",
  },
  {
    id: "fnd-judgment",
    tone: "crimson",
    name: "Judgment retained",
    rule: "Agents accelerate analysis; the three senior lenses own every conclusion. Challenge mode forces the question: what would disprove this?",
  },
  {
    id: "fnd-outcomes",
    tone: "neutral",
    name: "Six Outcomes governance",
    rule: "On time, on budget, on spec, no surprises, outstanding customer experience, business benefits achieved - every module reports into this scorecard.",
  },
];

export const SYSTEM_OUTPUTS = [
  { id: "out-evidence", name: "Evidence request register", detail: "22 categories with rationale, sensitivity flags, and owner routing", to: "/evidence" },
  { id: "out-findings", name: "Confidence-labeled finding set", detail: "Outputs from 12 agent lanes, each with evidence links and follow-ups", to: "/agents" },
  { id: "out-consensus", name: "Triad consensus outputs", detail: "Per-finding convergence with challenge records and decision chips", to: "/triad" },
  { id: "out-matrix", name: "23-dimension assessment", detail: "Rated capability heatmap with evidence strength per row", to: "/matrix" },
  { id: "out-registers", name: "Separated registers", detail: "12 gaps, 8 risks, 5 issues - owned, severity-rated, convert-to-action paths", to: "/control-tower" },
  { id: "out-pilots", name: "Gated pilot portfolio", detail: "9 scored plays; measurement plans with holdouts and stop rules", to: "/pilots" },
  { id: "out-change", name: "Adoption plan", detail: "6 phases, 26 change accelerators, stakeholder readiness map", to: "/brightspeed" },
  { id: "out-rhythm", name: "30-day operating rhythm", detail: "Day 1/3/5/7/10/15/20/30 gates with deliverables and owners", to: "/impact-engine" },
  { id: "out-comms", name: "Acid-tested communications", detail: "Upward, outward, downward, and customer channels on cadence", to: "/comms" },
  { id: "out-proof", name: "Day 15 pulse · Day 30 proof", detail: "Leading-indicator pulse and holdout-attributed proof report", to: "/reports" },
];

// Module Directory - the user manual. One entry per left-panel workspace, grouped exactly as the navigation.
export const MODULE_MANUAL = [
  {
    group: "Orientation",
    blurb: "Where the room gets set: the problem, the thesis, the people, and the operating model.",
    modules: [
      {
        to: "/briefing",
        name: "Consortium Briefing",
        purpose: "The working-room orientation: the ~18% cancellation problem, the operating thesis, the three senior personas with their signature questions, and the Six Outcomes that govern everything downstream.",
        runs_on: "Assessment state, methodology register, Six Outcomes scorecard",
        use_it_to: "Set the room, launch the assessment, and start the executive walkthrough",
        produces: "A shared operating thesis every stakeholder can repeat",
      },
      {
        to: "/flow",
        name: "Judgment to Execution",
        purpose: "The 12-step operating model: for each step, what SIX does, what Brightspeed provides, what AI supports, and the output produced. The loop closes - benefit tracking feeds new hypotheses.",
        runs_on: "Flow register (12 steps, hypothesis through benefit tracking)",
        use_it_to: "Explain the entire operating model to any stakeholder in three minutes",
        produces: "A defensible answer to 'how does this actually work?'",
      },
    ],
  },
  {
    group: "Intake & Analysis",
    blurb: "Where evidence enters, agents process, and senior judgment interrogates.",
    modules: [
      {
        to: "/evidence",
        name: "Evidence Room",
        purpose: "The 22-category request register sent after NDA: received, partial, and missing states with sensitivity flags, owner routing, and the decision each item supports.",
        runs_on: "Evidence register with per-item rationale and blocking relationships",
        use_it_to: "Chase outstanding items and see exactly what blocks pilot launch",
        produces: "A live data-confidence view - what is known vs still owed",
      },
      {
        to: "/launch",
        name: "Launch Console",
        purpose: "Scope guardrails, stakeholder groups, and the hypothesis register - six registered hypotheses with status, each upgradeable or downgradeable as evidence lands.",
        runs_on: "Launch configuration, hypothesis register, stakeholder map",
        use_it_to: "Confirm scope and hypotheses before Week 1 sessions begin",
        produces: "A locked launch configuration with named stakeholder groups",
      },
      {
        to: "/agents",
        name: "Agentic Analysis Studio",
        purpose: "Twelve agent lanes - metric integrity through executive synthesis - with staged runs, per-lane progress, outputs capped at the evidence they used, and routing into triad review.",
        runs_on: "AGT-01 through AGT-12 · agents + findings collections · live run trigger",
        use_it_to: "Trigger runs, challenge outputs, and route priority findings to the Triad",
        produces: "Findings with evidence links, confidence labels, and follow-ups",
      },
      {
        to: "/triad",
        name: "Triad Review Room",
        purpose: "Three-persona review per priority finding: Transformation Partner, AI/Data Strategist, and The Operator each interpret, then converge on a consensus block. Challenge mode surfaces counterfactuals.",
        runs_on: "Findings routed from the Analysis Studio · consensus synthesis",
        use_it_to: "Interrogate anything pilot-bound - push challenge mode before commitment",
        produces: "Validated consensus outputs: believe / know / don't know / do next",
      },
    ],
  },
  {
    group: "Assessment",
    blurb: "Where findings become ratings, drivers, and separated registers.",
    modules: [
      {
        to: "/matrix",
        name: "Assessment Matrix",
        purpose: "23 operating dimensions rated 1-5 (Weak to Leading) with evidence strength and recommended actions per row. Low-rated rows structure the sponsor conversation.",
        runs_on: "Matrix register fed by validated findings",
        use_it_to: "Show where the operating system needs decisions - amber and red rows first",
        produces: "A capability heatmap with an action attached to every weak dimension",
      },
      {
        to: "/analytics",
        name: "Analytics Workspace",
        purpose: "The cancellation timing curve with the day 8-14 danger zone, plus vendor, channel, market, save-timing, and fallout cuts - every chart carrying its confidence label and the fields needed to harden it.",
        runs_on: "Analytics generation over the order extract spine · confidence labeling",
        use_it_to: "Anchor every claim to its confidence - and read the danger zone",
        produces: "Driver views plus a field-level backlog for validated truth",
      },
      {
        to: "/control-tower",
        name: "Gap / Risk Control Tower",
        purpose: "Separated registers - 12 gaps, 8 risks, 5 issues - each with evidence, severity, and owner. One click converts a confirmed gap into an owned action with due date, dependencies, and measurement.",
        runs_on: "Gap / risk / issue registers · convert-to-action pipeline · decision queue",
        use_it_to: "Convert confirmed gaps and keep leadership decisions moving",
        produces: "An action log and decision queue - no finding stays an observation",
      },
    ],
  },
  {
    group: "Execution",
    blurb: "Where commitments become pilots, adoption, and a 30-day rhythm.",
    modules: [
      {
        to: "/questions",
        name: "Question Builder",
        purpose: "Week 1 interview packs by audience - schedulers, save teams, vendor managers, executives - with each question mapped to the evidence gap it closes.",
        runs_on: "Question register mapped to evidence gaps and hypotheses",
        use_it_to: "Assemble packs and assign interviewers by persona lens",
        produces: "Field-ready question sets that close specific blind spots",
      },
      {
        to: "/pilots",
        name: "Pilot Board",
        purpose: "Nine plays scored across nine dimensions, with gate status, dependencies, and full measurement designs - holdout cohorts, leading indicators, and stop rules. The Triad gate holds until measurement is locked.",
        runs_on: "Pilot register · nine-dimension scoring · measurement plans",
        use_it_to: "Hold the gate - nothing launches without measurement locked",
        produces: "A gated pilot portfolio with four plays 30-day ready",
      },
      {
        to: "/brightspeed",
        name: "Change Management",
        purpose: "SIX's Accelerating Adoption approach applied to Brightspeed: six phases, 26 change accelerators, a stakeholder readiness map, and adoption metrics designed into every pilot with pause-and-fix thresholds.",
        runs_on: "Change phase register · accelerator library · readiness assessments",
        use_it_to: "Run adoption by plan - know who resists before day one, not day twenty",
        produces: "A change plan gated like a delivery plan, with adoption measured",
      },
      {
        to: "/impact-engine",
        name: "30-Day Impact Engine",
        purpose: "The milestone rail: Day 1 mobilize, Day 5 gap scan, Day 7 cohorts and decisions, Day 10 first-wave launch, Day 15 indicator pulse, Day 20 scale/stop/adjust, Day 30 proof report.",
        runs_on: "Timeline register with per-gate deliverables, owners, and status",
        use_it_to: "Protect the gates - decision velocity is the job",
        produces: "A living 30-day rhythm with named deliverables at every node",
      },
    ],
  },
  {
    group: "Executive",
    blurb: "Where leadership sees status, signs communication, and receives proof.",
    modules: [
      {
        to: "/command-center",
        name: "Command Center",
        purpose: "The Monday view: baseline status, evidence completeness, top drivers vs unknowns, open decisions, pilot readiness, the Six Outcomes scorecard, and what changed since last review.",
        runs_on: "Every register in the system, synthesized by AGT-12",
        use_it_to: "Review what changed, then move straight to open decisions",
        produces: "Executive status at a glance - outcomes, confidence, blockers",
      },
      {
        to: "/comms",
        name: "Communications Studio",
        purpose: "Four channels - upward to the board, outward to functions, downward to frontline teams, and customer-facing - with acid-test scoring on every artifact before it ships.",
        runs_on: "Communication register · acid-test scoring · channel cadences",
        use_it_to: "Sign the sponsor update and ship huddle cards that teams actually use",
        produces: "Acid-tested artifacts on cadence across all four channels",
      },
      {
        to: "/reports",
        name: "Report Builder",
        purpose: "The credibility artifacts: the Day 15 leading-indicator pulse and the Day 30 proof report - movement claimed only against holdout cohorts, gaps and unknowns declared.",
        runs_on: "Report register assembled from pilots, registers, and measurement",
        use_it_to: "Deliver proof to the proof standard - no theater",
        produces: "Export-ready executive reports with declared confidence",
      },
    ],
  },
];
